import { motion } from "framer-motion";
import { Users, Crosshair, BarChart3, Network } from "lucide-react";

const CHAPTERS = [
  {
    num: "01",
    title: "ARTIST DEVELOPMENT",
    icon: Users,
    body: "Hands-on and personal. We build the human before the brand — sound, story, stage presence, discipline. No shortcuts, no templates.",
    tag: "PROCESS // HANDS-ON",
  },
  {
    num: "02",
    title: "A&R STRATEGY",
    icon: Crosshair,
    body: "We find what the industry overlooks. Records positioned to cut through — the right sound, the right moment, the right market.",
    tag: "INSTINCT // SHARPENED",
  },
  {
    num: "03",
    title: "DATA ANALYSIS",
    icon: BarChart3,
    body: "Powered by Aidar.ai. Gut feeling verified by numbers — audience signals, market gaps, and catalog performance read in real time.",
    tag: "SOURCE // AIDAR.AI",
  },
  {
    num: "04",
    title: "RELATIONSHIP MARKETING",
    icon: Network,
    body: "Doors open because of who picks up the phone. Two decades of trust across labels, touring, and management — from San Diego to Shaolin.",
    tag: "NETWORK // EARNED",
  },
];

export default function Manifesto() {
  return (
    <section id="manifesto" className="relative py-28 sm:py-36" data-testid="manifesto-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-16"
        >
          <p className="mono text-[11px] tracking-[0.35em] text-[#00F0FF] mb-4" data-testid="manifesto-eyebrow">
            THE MANIFESTO // FOUR WEAPONS
          </p>
          <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]" data-testid="manifesto-heading">
            WEAPONS OF <span className="text-stroke-faint">MASS</span> CREATION
          </h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 gap-px bg-[#22222B]">
          {CHAPTERS.map((c, i) => (
            <motion.article
              key={c.num}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="group relative bg-[#0C0C10] p-8 sm:p-10 hover:bg-[#111115] transition-colors duration-500"
              data-testid={`manifesto-chapter-${c.num}`}
            >
              <div className="flex items-start justify-between mb-10">
                <span className="font-display text-5xl text-stroke-cyan group-hover:text-[#00F0FF] transition-colors duration-500" style={{ WebkitTextStroke: undefined }}>
                  {c.num}
                </span>
                <c.icon className="h-6 w-6 text-[#60606E] group-hover:text-[#CCFF00] transition-colors duration-500" />
              </div>
              <h3 className="font-display uppercase text-2xl sm:text-3xl mb-4 tracking-wide">{c.title}</h3>
              <p className="text-sm text-[#A0A0AB] leading-relaxed mb-8 max-w-md">{c.body}</p>
              <p className="mono text-[10px] tracking-[0.3em] text-[#60606E]">{c.tag}</p>
              <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-[#00F0FF] transition-all duration-700 group-hover:w-full" />
            </motion.article>
          ))}
        </div>

        <motion.blockquote
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.2 }}
          className="mt-24 sm:mt-32 max-w-4xl"
          data-testid="manifesto-quote"
        >
          <p className="font-display uppercase text-3xl sm:text-5xl leading-tight text-[#F2F2F5]">
            “WE LOVE THIS LIFE AND WE AIN'T PERFECT BUT{" "}
            <span className="text-[#CCFF00]">WE GETTING THERE.</span>”
          </p>
          <cite className="mono text-[11px] tracking-[0.3em] text-[#60606E] not-italic mt-6 block">
            — ONCORE, FOUNDER // PLAGIARHYTHM
          </cite>
        </motion.blockquote>
      </div>
    </section>
  );
}
